import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resetToken, setResetToken] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
      // Store token for testing (in production this would be emailed)
      if (response.data.reset_token) {
        setResetToken(response.data.reset_token);
      }
      toast({
        title: "Reset link generated",
        description: "Check below for your password reset link.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4" data-testid="forgot-password-success">
        <Card className="w-full max-w-md border-2">
          <CardContent className="p-6 sm:p-8 text-center">
            <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Reset Link Generated</h2>
            <p className="text-slate-600 mb-6 text-sm">
              If an account exists for <strong>{email}</strong>, a password reset link has been created.
            </p>

            {resetToken && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6 text-left">
                <p className="text-xs text-slate-500 mb-2 font-medium">Your reset link (copy and open in browser):</p>
                <p className="text-xs text-purple-700 break-all font-mono bg-white p-2 rounded border" data-testid="reset-link">
                  {window.location.origin}/reset-password?token={resetToken}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 w-full text-xs"
                  data-testid="copy-reset-link-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/reset-password?token=${resetToken}`);
                    toast({ title: "Copied!", description: "Reset link copied to clipboard." });
                  }}
                >
                  Copy Reset Link
                </Button>
              </div>
            )}

            <Link to="/login">
              <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="back-to-login-btn">
                Back to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4" data-testid="forgot-password-page">
      <Card className="w-full max-w-md border-2">
        <CardHeader>
          <Link to="/login" className="text-slate-500 hover:text-slate-700 text-sm flex items-center gap-1 mb-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Login
          </Link>
          <CardTitle className="text-2xl">Forgot Password</CardTitle>
          <p className="text-slate-500 text-sm mt-1">Enter your email and we'll generate a reset link for you.</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email Address</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                  disabled={loading}
                  data-testid="forgot-password-email-input"
                />
              </div>
            </div>
            <Button
              type="submit"
              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              disabled={loading}
              data-testid="forgot-password-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Generate Reset Link'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
